import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, XCircle, Clock, Image, Film, Music, Sparkles, Loader2, Trash2, Play, Pause } from 'lucide-react';
import { toast } from 'sonner';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyWallpapers, WallpapersResponse, updateWallpapers } from '@/lib/collections/wallpapers';
import { Time, Address } from '@/lib/db-client';
import { useAuth, getIdToken } from '@pooflabs/web';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import { CreatorLabel } from '@/components/CreatorLabel';
import { CreatorAvatar } from '@/components/CreatorAvatar';

type WallpaperWithStatus = WallpapersResponse;

interface AdminModerationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with true when the full-view preview opens, false when it closes */
  onFullViewChange?: (isOpen: boolean) => void;
}

// ────────────────────────────────────────────────────────────
// Thumbnail card — compact grid tile
// ────────────────────────────────────────────────────────────
const ThumbnailCard: React.FC<{
  wallpaper: WallpaperWithStatus;
  onClick: () => void;
  index: number;
}> = ({ wallpaper, onClick, index }) => {
  const isImage = !wallpaper.mediaType || wallpaper.mediaType === 'wallpaper' || wallpaper.mediaType === 'icon' || wallpaper.mediaType === 'image';
  const isVideo = wallpaper.mediaType === 'live-wallpaper';
  const isAudio = wallpaper.mediaType === 'ringtone';

  const getTypeIcon = () => {
    if (isVideo) return <Film size={14} color="#a78bfa" />;
    if (isAudio) return <Music size={14} color="#a78bfa" />;
    if (wallpaper.mediaType === 'icon') return <Sparkles size={14} color="#a78bfa" />;
    return null;
  };

  const typeIcon = getTypeIcon();

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.88 }}
      transition={{ delay: index * 0.04, duration: 0.2 }}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      style={{
        position: 'relative',
        aspectRatio: '1 / 1',
        width: '100%',
        borderRadius: 10,
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(255,255,255,0.03)',
        cursor: 'pointer',
        padding: 0,
        display: 'block',
      }}
    >
      {/* Thumbnail image */}
      {(isImage || isVideo) && wallpaper.imageUrl ? (
        <img
          src={wallpaper.thumbnailUrl || wallpaper.imageUrl}
          alt={wallpaper.title}
          loading="lazy"
          decoding="async"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={e => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      ) : isAudio ? (
        <div style={{
          width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(167,139,250,0.08)',
        }}>
          <Music size={22} color="#a78bfa" strokeWidth={1.5} />
        </div>
      ) : (
        <div style={{
          width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(167,139,250,0.08)',
        }}>
          <Image size={22} color="rgba(167,139,250,0.5)" strokeWidth={1.5} />
        </div>
      )}

      {/* Hover overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 55%)',
        opacity: 0,
        transition: 'opacity 0.15s ease',
      }}
        onMouseEnter={e => ((e.currentTarget as HTMLDivElement).style.opacity = '1')}
        onMouseLeave={e => ((e.currentTarget as HTMLDivElement).style.opacity = '0')}
      />

      {/* Type badge */}
      {typeIcon && (
        <div style={{
          position: 'absolute', top: 5, right: 5,
          width: 20, height: 20, borderRadius: 5,
          background: 'rgba(0,0,0,0.65)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(167,139,250,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {typeIcon}
        </div>
      )}
    </motion.button>
  );
};

// ────────────────────────────────────────────────────────────
// Full-view modal — image only + two action buttons
// ────────────────────────────────────────────────────────────
const formatDuration = (seconds: number): string => {
  if (!isFinite(seconds) || seconds < 0) return '--:--';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const getCategoryLabel = (mediaType?: string): string => {
  if (mediaType === 'live-wallpaper') return 'Live Wallpaper';
  if (mediaType === 'ringtone') return 'Ringtone';
  if (mediaType === 'icon') return 'Icon';
  return 'Wallpaper';
};

const FullViewModal: React.FC<{
  wallpaper: WallpaperWithStatus | null;
  pendingList: WallpaperWithStatus[];
  adminAddress: string;
  onClose: () => void;
  onAdvance: (next: WallpaperWithStatus | null) => void;
}> = ({ wallpaper, pendingList, adminAddress, onClose, onAdvance }) => {
  const [loading, setLoading] = useState<'accept' | 'delete' | null>(null);
  const { user } = useAuth();
  const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null);
  const [videoDims, setVideoDims] = useState<{ w: number; h: number } | null>(null);
  const [audioDuration, setAudioDuration] = useState<number | null>(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioTime, setAudioTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Reset media metadata whenever the previewed item changes
  useEffect(() => {
    setImgDims(null);
    setVideoDims(null);
    setAudioDuration(null);
    setAudioPlaying(false);
    setAudioTime(0);
  }, [wallpaper?.id]);

  // Esc to close
  useEffect(() => {
    if (!wallpaper) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [wallpaper, onClose]);

  if (!wallpaper) return null;

  const isImage = !wallpaper.mediaType || wallpaper.mediaType === 'wallpaper' || wallpaper.mediaType === 'icon' || wallpaper.mediaType === 'image';
  const isVideo = wallpaper.mediaType === 'live-wallpaper';
  const isAudio = wallpaper.mediaType === 'ringtone';
  const isIcon = wallpaper.mediaType === 'icon';

  const categoryLabel = getCategoryLabel(wallpaper.mediaType);
  const userCategory = wallpaper.category;

  // Quality string: resolution for image/video, duration for audio
  let qualityLabel: string | null = null;
  if (isImage && imgDims) qualityLabel = `${imgDims.w} × ${imgDims.h}`;
  else if (isVideo && videoDims) qualityLabel = `${videoDims.w} × ${videoDims.h}`;
  else if (isAudio && audioDuration != null) qualityLabel = formatDuration(audioDuration);

  const handleModerate = async (action: 'accept' | 'delete') => {
    if (!wallpaper) return;
    setLoading(action);
    try {
      let ok = false;

      if (action === 'accept') {
        // Use the backend API for approval — it handles fan-out notifications atomically
        const token = await getIdToken();
        if (!token || !user?.address) {
          toast.error('Authentication error. Please reconnect your wallet.');
          return;
        }
        const authApi = createAuthenticatedApiClient(token, user.address);
        await authApi.post(`/api/admin/wallpapers/${wallpaper.id}/approve`, {});
        ok = true;
      } else {
        // Decline uses direct collection update (no fan-out needed)
        ok = await updateWallpapers(wallpaper.id, {
          status: 'declined',
          reviewedAt: Time.Now,
          reviewedBy: Address.publicKey(adminAddress),
        } as any);
      }

      if (ok) {
        // Find the next pending item (exclude the one just moderated)
        const remaining = pendingList.filter(w => w.id !== wallpaper.id);
        if (remaining.length > 0) {
          // Find the item that was after the current one in the list, or fall back to first
          const currentIndex = pendingList.findIndex(w => w.id === wallpaper.id);
          const nextIndex = currentIndex < remaining.length ? currentIndex : 0;
          onAdvance(remaining[nextIndex]);
        } else {
          onAdvance(null);
        }
      } else {
        toast.error('Failed to update status. Check permissions.');
      }
    } catch (err) {
      console.error('Moderation error:', err);
      toast.error('An error occurred during moderation');
    } finally {
      setLoading(null);
    }
  };

  const toggleAudio = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play();
    else el.pause();
  };

  return (
    <AnimatePresence>
      {wallpaper && (
        <motion.div
          key="moderation-fullscreen"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 6000,
            background: '#000',
            overflow: 'hidden',
          }}
        >
          {/* Full-bleed media — fills entire viewport like the real wallpaper */}
          {isImage && wallpaper.imageUrl ? (
            <img
              src={wallpaper.imageUrl}
              alt={wallpaper.title}
              loading="lazy"
              decoding="async"
              onLoad={e => {
                const t = e.currentTarget as HTMLImageElement;
                setImgDims({ w: t.naturalWidth, h: t.naturalHeight });
              }}
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: isIcon ? 'contain' : 'cover',
                display: 'block',
              }}
            />
          ) : isVideo && wallpaper.imageUrl ? (
            <video
              src={wallpaper.imageUrl}
              autoPlay
              loop
              muted
              playsInline
              onLoadedMetadata={e => {
                const t = e.currentTarget as HTMLVideoElement;
                setVideoDims({ w: t.videoWidth, h: t.videoHeight });
              }}
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : isAudio ? (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'radial-gradient(ellipse at center, rgba(167,139,250,0.22) 0%, rgba(12,11,24,1) 65%)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 28, padding: 32,
            }}>
              <motion.button
                onClick={toggleAudio}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  width: 168, height: 168, borderRadius: '50%',
                  background: 'rgba(167,139,250,0.14)',
                  border: '2px solid rgba(167,139,250,0.45)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 0 80px rgba(167,139,250,0.35)',
                }}
              >
                {audioPlaying
                  ? <Pause size={64} color="#e9defc" strokeWidth={1.5} />
                  : <Play size={64} color="#e9defc" strokeWidth={1.5} style={{ marginLeft: 6 }} />}
              </motion.button>
              <div style={{ textAlign: 'center' }}>
                <p style={{
                  margin: 0, fontSize: 22, fontWeight: 700, color: '#f0f0f5',
                  fontFamily: "'Syne', sans-serif", letterSpacing: '-0.01em',
                }}>
                  {wallpaper.title}
                </p>
                <p style={{
                  margin: '6px 0 0', fontSize: 13,
                  color: 'rgba(240,240,245,0.5)',
                  fontFamily: "'DM Mono', monospace",
                }}>
                  {audioDuration != null
                    ? `${formatDuration(audioTime)} / ${formatDuration(audioDuration)}`
                    : '--:-- / --:--'}
                </p>
              </div>
              {wallpaper.imageUrl && (
                <audio
                  ref={audioRef}
                  src={wallpaper.imageUrl}
                  onLoadedMetadata={e => setAudioDuration((e.currentTarget as HTMLAudioElement).duration)}
                  onPlay={() => setAudioPlaying(true)}
                  onPause={() => setAudioPlaying(false)}
                  onTimeUpdate={e => setAudioTime((e.currentTarget as HTMLAudioElement).currentTime)}
                  loop
                />
              )}
            </div>
          ) : (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#0c0b18',
            }}>
              <Image size={64} color="rgba(167,139,250,0.3)" strokeWidth={1.5} />
            </div>
          )}

          {/* Subtle gradient from bottom for legibility of overlay */}
          <div style={{
            position: 'absolute', left: 0, right: 0, bottom: 0,
            height: '40%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.45) 45%, transparent 100%)',
            pointerEvents: 'none',
          }} />

          {/* Close button (top-right overlay) */}
          <motion.button
            whileHover={{ scale: 1.08, backgroundColor: 'rgba(0,0,0,0.7)' }}
            whileTap={{ scale: 0.92 }}
            onClick={onClose}
            style={{
              position: 'absolute', top: 20, right: 20, zIndex: 10,
              width: 40, height: 40, borderRadius: 10,
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#f0f0f5',
            }}
            title="Close (Esc)"
          >
            <X size={18} />
          </motion.button>

          {/* Title + uploader (top-left overlay) */}
          <div style={{
            position: 'absolute', top: 20, left: 20, zIndex: 10,
            maxWidth: 'calc(100vw - 100px)',
            padding: '10px 14px',
            borderRadius: 12,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}>
            <p style={{
              margin: 0, fontSize: 14, fontWeight: 700, color: '#f0f0f5',
              fontFamily: "'Syne', sans-serif",
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {wallpaper.title}
            </p>
            {wallpaper.uploadedBy && (
              <div style={{
                marginTop: 4,
                display: 'flex', alignItems: 'center', gap: 5,
              }}>
                <span style={{
                  fontSize: 10,
                  color: 'rgba(240,240,245,0.4)',
                  fontFamily: "'DM Mono', monospace",
                }}>
                  by
                </span>
                <CreatorAvatar walletAddress={wallpaper.uploadedBy} size={16} />
                <CreatorLabel
                  walletAddress={wallpaper.uploadedBy}
                  style={{ fontSize: 11 }}
                />
              </div>
            )}
          </div>

          {/* Bottom overlay — metadata + Accept/Decline */}
          <div
            style={{
              position: 'absolute', left: 0, right: 0, bottom: 0,
              zIndex: 10,
              padding: '20px max(20px, env(safe-area-inset-left)) max(20px, env(safe-area-inset-bottom))',
              display: 'flex', flexDirection: 'column', gap: 14,
              alignItems: 'center',
            }}
          >
            {/* Metadata badges */}
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center',
            }}>
              {/* Type badge */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 12px',
                borderRadius: 999,
                background: 'rgba(167,139,250,0.18)',
                border: '1px solid rgba(167,139,250,0.4)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                color: '#d8c8ff',
                fontSize: 11, fontWeight: 700,
                fontFamily: "'Syne', sans-serif",
                letterSpacing: '0.04em', textTransform: 'uppercase',
              }}>
                {isVideo ? <Film size={12} /> : isAudio ? <Music size={12} /> : isIcon ? <Sparkles size={12} /> : <Image size={12} />}
                {categoryLabel}
              </div>

              {/* User-supplied category */}
              {userCategory && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '6px 12px',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  color: '#f0f0f5',
                  fontSize: 11, fontWeight: 600,
                  fontFamily: "'Syne', sans-serif",
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                }}>
                  {userCategory}
                </div>
              )}

              {/* Quality / resolution / duration */}
              {qualityLabel && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '6px 12px',
                  borderRadius: 999,
                  background: 'rgba(0,0,0,0.45)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  color: 'rgba(240,240,245,0.85)',
                  fontSize: 11, fontWeight: 600,
                  fontFamily: "'DM Mono', monospace",
                  letterSpacing: '0.02em',
                }}>
                  {qualityLabel}
                </div>
              )}
            </div>

            {/* Accept / Decline buttons */}
            <div style={{
              display: 'flex', gap: 12,
              width: '100%', maxWidth: 460,
            }}>
              <motion.button
                whileHover={loading ? {} : { scale: 1.02, backgroundColor: 'rgba(34,197,94,0.28)' }}
                whileTap={loading ? {} : { scale: 0.97 }}
                onClick={() => handleModerate('accept')}
                disabled={!!loading}
                style={{
                  flex: 1,
                  padding: '15px 16px',
                  borderRadius: 14,
                  background: 'rgba(34,197,94,0.18)',
                  border: '1px solid rgba(34,197,94,0.5)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  color: '#4ade80',
                  fontSize: 14, fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: "'Syne', sans-serif",
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                }}
              >
                {loading === 'accept' ? (
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Check size={16} strokeWidth={2.5} />
                )}
                Accept
              </motion.button>

              <motion.button
                whileHover={loading ? {} : { scale: 1.02, backgroundColor: 'rgba(239,68,68,0.28)' }}
                whileTap={loading ? {} : { scale: 0.97 }}
                onClick={() => handleModerate('delete')}
                disabled={!!loading}
                style={{
                  flex: 1,
                  padding: '15px 16px',
                  borderRadius: 14,
                  background: 'rgba(239,68,68,0.16)',
                  border: '1px solid rgba(239,68,68,0.5)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  color: '#f87171',
                  fontSize: 14, fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: "'Syne', sans-serif",
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                }}
              >
                {loading === 'delete' ? (
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Trash2 size={16} strokeWidth={2} />
                )}
                Decline
              </motion.button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ────────────────────────────────────────────────────────────
// Main panel
// ────────────────────────────────────────────────────────────
export const AdminModerationPanel: React.FC<AdminModerationPanelProps> = ({ isOpen, onClose, onFullViewChange }) => {
  const { user } = useAuth();
  const [selected, setSelected] = useState<WallpaperWithStatus | null>(null);

  // Notify parent whenever the full-view preview opens or closes
  useEffect(() => {
    onFullViewChange?.(selected !== null);
  }, [selected, onFullViewChange]);

  const { data: allWallpapers } = useRealtimeData<WallpaperWithStatus[]>(
    subscribeManyWallpapers,
    isOpen
  );

  const pendingWallpapers = (allWallpapers ?? []).filter(w => {
    const s = (w as WallpaperWithStatus).status;
    return s === 'pending' || s == null;
  });

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={onClose}
              style={{
                position: 'fixed', inset: 0, zIndex: 5200,
                background: 'rgba(0,0,0,0.75)',
                backdropFilter: 'blur(10px)',
              }}
            />

            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ type: 'spring', stiffness: 350, damping: 32 }}
              style={{
                position: 'fixed', top: 0, right: 0, bottom: 0,
                width: 'min(460px, 100vw)',
                zIndex: 5201,
                background: '#0c0b18',
                borderLeft: '1px solid rgba(167,139,250,0.15)',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <div style={{
                padding: '20px 20px 16px',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexShrink: 0,
              }}>
                <div>
                  <h3 style={{
                    fontSize: 17, fontWeight: 800, color: '#f0f0f5', margin: 0,
                    fontFamily: "'Syne', sans-serif", letterSpacing: '-0.02em',
                  }}>
                    Moderation
                  </h3>
                  <p style={{
                    fontSize: 12, color: 'rgba(167,139,250,0.6)', margin: '2px 0 0',
                    fontFamily: "'DM Mono', monospace",
                  }}>
                    {pendingWallpapers.length} pending submission{pendingWallpapers.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: 'rgba(240,240,245,0.5)',
                  }}
                >
                  <X size={15} />
                </motion.button>
              </div>

              {/* Thumbnail grid */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
                {pendingWallpapers.length === 0 ? (
                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    height: '100%', gap: 12, paddingTop: 60,
                  }}>
                    <div style={{
                      width: 60, height: 60, borderRadius: '50%',
                      background: 'rgba(167,139,250,0.1)',
                      border: '1px solid rgba(167,139,250,0.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Clock size={26} color="#a78bfa" strokeWidth={1.5} />
                    </div>
                    <p style={{
                      fontSize: 14, fontWeight: 700, color: 'rgba(240,240,245,0.5)',
                      margin: 0, fontFamily: "'Syne', sans-serif", textAlign: 'center',
                    }}>
                      No pending submissions
                    </p>
                    <p style={{
                      fontSize: 12, color: 'rgba(240,240,245,0.3)',
                      margin: 0, fontFamily: "'DM Mono', monospace", textAlign: 'center',
                    }}>
                      All content has been reviewed
                    </p>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 8,
                  }}>
                    <AnimatePresence mode="popLayout">
                      {pendingWallpapers.map((w, i) => (
                        <ThumbnailCard
                          key={w.id}
                          wallpaper={w}
                          index={i}
                          onClick={() => setSelected(w)}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Full-view modal — rendered outside the panel so it can cover everything */}
      <FullViewModal
        wallpaper={selected}
        pendingList={pendingWallpapers}
        adminAddress={user?.address ?? ''}
        onClose={() => setSelected(null)}
        onAdvance={(next) => setSelected(next)}
      />
    </>
  );
};
